import React from 'react';

export function FermentationIssues() {
  return (
    <div className="space-y-6 text-white/90" style={{ userSelect: "none" }}>
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Fermentation Issues</h3>
        <p className="mb-4">
          Fermentation is the heart of sourdough baking, and many common bread problems can be traced back to fermentation issues. Understanding these problems and their solutions will help you achieve more consistent results.
        </p>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Under-Fermentation</h3>
        
        <div className="space-y-4">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Signs of Under-Fermentation</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Visual Indicators:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Dense, gummy crumb</li>
                  <li>Large irregular holes</li>
                  <li>Pale crust color</li>
                  <li>Minimal aroma</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Common Causes:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Insufficient bulk fermentation time</li>
                  <li>Cold environment</li>
                  <li>Weak or insufficient starter</li>
                  <li>Too much salt</li>
                </ul>
              </div>
            </div>
            <div className="mt-4">
              <h5 className="text-amber-400 text-sm">Solutions:</h5>
              <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                <li>Extend bulk fermentation time</li>
                <li>Use warmer water or find warmer spot</li>
                <li>Feed starter more frequently before baking</li>
                <li>Verify salt measurements</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Over-Fermentation</h3>
        
        <div className="space-y-4">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Signs of Over-Fermentation</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Visual Indicators:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Flat, spread-out loaf</li>
                  <li>Overly sour taste</li>
                  <li>Sticky, wet crumb</li>
                  <li>Poor oven spring</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Common Causes:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Too long bulk fermentation</li>
                  <li>Too warm environment</li>
                  <li>Too much starter</li>
                  <li>Extended final proof</li>
                </ul>
              </div>
            </div>
            <div className="mt-4">
              <h5 className="text-amber-400 text-sm">Solutions:</h5>
              <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                <li>Reduce fermentation time</li>
                <li>Use cooler water or environment</li>
                <li>Reduce starter percentage</li>
                <li>Monitor dough temperature</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Uneven Fermentation</h3>
        
        <div className="space-y-4">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Signs of Uneven Fermentation</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Visual Indicators:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Large irregular holes</li>
                  <li>Dense areas in crumb</li>
                  <li>Inconsistent texture</li>
                  <li>Uneven crust color</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Common Causes:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Poor starter distribution</li>
                  <li>Inconsistent folding</li>
                  <li>Temperature fluctuations</li>
                  <li>Improper dough handling</li>
                </ul>
              </div>
            </div>
            <div className="mt-4">
              <h5 className="text-amber-400 text-sm">Solutions:</h5>
              <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                <li>Mix starter thoroughly into dough</li>
                <li>Perform folds consistently</li>
                <li>Maintain stable temperature</li>
                <li>Handle dough gently but thoroughly</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Temperature-Related Issues</h3>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white/5 p-4 rounded-lg">
              <h4 className="text-lg text-amber-400 mb-2">Cold Environment</h4>
              <div>
                <h5 className="text-amber-400 text-sm">Effects:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Slow fermentation</li>
                  <li>Extended proofing times</li>
                  <li>Weak gluten development</li>
                  <li>Poor flavor development</li>
                </ul>
              </div>
              <div className="mt-3">
                <h5 className="text-amber-400 text-sm">Solutions:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Use warmer water</li>
                  <li>Create warm environment</li>
                  <li>Increase starter percentage</li>
                  <li>Extend fermentation time</li>
                </ul>
              </div>
            </div>
            
            <div className="bg-white/5 p-4 rounded-lg">
              <h4 className="text-lg text-amber-400 mb-2">Hot Environment</h4>
              <div>
                <h5 className="text-amber-400 text-sm">Effects:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Rapid fermentation</li>
                  <li>Over-acidic flavor</li>
                  <li>Sticky dough</li>
                  <li>Poor structure</li>
                </ul>
              </div>
              <div className="mt-3">
                <h5 className="text-amber-400 text-sm">Solutions:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Use cold water</li>
                  <li>Reduce starter percentage</li>
                  <li>Shorten fermentation time</li>
                  <li>Consider refrigeration</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-amber-400 mb-4">Monitoring Fermentation</h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="text-lg text-white mb-2">Visual Indicators</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Dough volume increase (30-50%)</li>
              <li>Surface bubbles</li>
              <li>Domed surface</li>
              <li>Jiggly texture</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">Tactile Tests</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Gentle poke test (should spring back slowly)</li>
              <li>Window pane test for gluten development</li>
              <li>Dough temperature checks</li>
              <li>Surface tension assessment</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">Timing Guidelines</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Room temperature (75°F/24°C): 4-6 hours bulk</li>
              <li>Cool room (65°F/18°C): 6-8 hours bulk</li>
              <li>Cold fermentation: 12-48 hours</li>
              <li>Final proof: 2-4 hours at room temperature</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}